import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import WeatherWidget from '../components/WeatherWidget';

const DEFAULT_HOMEPAGE = {
  hero: {
    badge: 'El marketplace #1 de Costa Rica',
    title: 'Repuestos para tu <span class="text-primary">moto</span>',
    subtitle: 'Miles de productos de vendedores verificados.',
    ctaText: 'Ver Productos',
    ctaSecondaryText: 'Vender',
    emoji: '🏍️'
  },
  seBusca: {
    enabled: true,
    title: '🔍 SE BUSCA',
    subtitle: 'Ver las piezas que la gente necesita →'
  },
  cta: {
    enabled: true,
    title: '¿Tienes repuestos para vender?',
    subtitle: 'Únete a miles de vendedores y llega a compradores interesados en tu zona.',
    buttonText: 'Crear cuenta gratis →'
  }
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [features, setFeatures] = useState([]);
  const [homepage, setHomepage] = useState(DEFAULT_HOMEPAGE);
  const [loading, setLoading] = useState(true);
  const [featuredHasMore, setFeaturedHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [heroImages, setHeroImages] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [shops, setShops] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [promotedAccessories, setPromotedAccessories] = useState([]);

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = () => {
    setLoading(true);
    Promise.all([
      api.get('/products/featured?page=1'),
      api.get('/products?sort=newest&limit=8'),
      api.get('/categories'),
      api.get('/site/features'),
      api.get('/site/homepage'),
      api.get('/hero-images')
    ]).then(([featuredRes, newProductsRes, categoriesRes, featuresRes, homepageRes, heroRes]) => {
      setFeatured(featuredRes.data?.products || []);
      setFeaturedHasMore(featuredRes.data?.hasMore || false);
      setNewProducts(newProductsRes.data?.products || newProductsRes.data || []);
      setCategories(categoriesRes.data || []);
      setFeatures(featuresRes.data?.features || []);
      setHomepage(homepageRes.data?.content || DEFAULT_HOMEPAGE);
      setHeroImages(heroRes.data?.images || []);
      setLoading(false);
    }).catch(() => setLoading(false));
    api.get('/shops').then(r => setShops(r.data || [])).catch(() => {});
    api.get('/products/accessories?promoted=true&sort=popular&limit=12').then(r => setPromotedAccessories(r.data?.products || [])).catch(() => {});
  };

  useEffect(() => {
    if (heroImages.length < 2) return;
    const t = setInterval(() => setHeroIndex(i => (i + 1) % heroImages.length), 5000);
    return () => clearInterval(t);
  }, [heroImages]);

  const loadMoreFeatured = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    api.get('/products/featured?page=0')
      .then(res => {
        setFeatured(res.data?.products || []);
        setFeaturedHasMore(res.data?.hasMore || false);
      })
      .catch(err => console.error(err))
      .finally(() => setLoadingMore(false));
  };

  const brands = [...new Set(categories.map(c => c.brand))];

  return (
    <div className="bg-[#F5F5F5]">

      {/* Hero */}
      <section className="relative text-white py-10 md:py-16 overflow-hidden" style={{minHeight: '280px'}}>
        {/* Crossfade backgrounds */}
        {heroImages.length > 0 ? heroImages.map((img, i) => (
          <div key={img} className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ backgroundImage: `url(${img})`, opacity: i === heroIndex ? 1 : 0 }} />
        )) : (
          <div className="absolute inset-0 bg-[#111]" />
        )}
        <div className="absolute inset-0 bg-black/60" />
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4" style={{zIndex: 2}}>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 text-center md:text-left">
              <span className="inline-block bg-primary text-white px-3 py-1 text-xs font-bold uppercase tracking-widest mb-3">
                {homepage.hero?.badge || DEFAULT_HOMEPAGE.hero.badge}
              </span>
              <h1 className="text-4xl md:text-5xl font-black mb-3 leading-tight uppercase tracking-tight">
                <span dangerouslySetInnerHTML={{ __html: homepage.hero?.title || DEFAULT_HOMEPAGE.hero.title }} />
              </h1>
              <p className="text-gray-300 text-sm mb-6 max-w-lg">
                {homepage.hero?.subtitle || DEFAULT_HOMEPAGE.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <Link to="/products" className="btn-primary text-center">
                  {homepage.hero?.ctaText || DEFAULT_HOMEPAGE.hero.ctaText}
                </Link>
                <Link to="/seller/products/new" className="border-2 border-gray-400 text-gray-200 px-6 py-3 font-bold uppercase text-sm tracking-wide hover:border-white hover:text-white transition text-center">
                  {homepage.hero?.ctaSecondaryText || DEFAULT_HOMEPAGE.hero.ctaSecondaryText}
                </Link>
              </div>
            </div>
            {heroImages.length === 0 && <div className="text-7xl">{homepage.hero?.emoji || DEFAULT_HOMEPAGE.hero.emoji}</div>}
            {heroImages.length > 0 && <WeatherWidget />}
          </div>
          {/* Dots */}
          {heroImages.length > 1 && (
            <div className="flex gap-2 mt-6 justify-center md:justify-start">
              {heroImages.map((_, i) => (
                <button key={i} onClick={() => setHeroIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === heroIndex ? 'bg-primary w-4' : 'bg-white/50'}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features strip */}
      {features.length > 0 && (
        <section className="bg-primary py-2.5">
          <div className="max-w-7xl mx-auto px-4">
            <div className={`grid gap-2 text-center text-white ${features.length === 2 ? 'grid-cols-2' : features.length === 3 ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`}>
              {features.map((f) => (
                <div key={f.id} className="flex items-center justify-center gap-1.5">
                  <span>{f.icon}</span>
                  <span className="font-bold text-xs uppercase tracking-wide">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SE BUSCA banner */}
      {homepage.seBusca?.enabled !== false && (
        <section className="py-4 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 flex gap-3 items-stretch">
            <style>{`
              @keyframes float1 { 0%,100%{transform:translate(0,0)} 33%{transform:translate(18px,-12px)} 66%{transform:translate(-10px,8px)} }
              @keyframes float2 { 0%,100%{transform:translate(0,0)} 33%{transform:translate(-14px,10px)} 66%{transform:translate(20px,-8px)} }
              @keyframes float3 { 0%,100%{transform:translate(0,0)} 33%{transform:translate(10px,14px)} 66%{transform:translate(-18px,-6px)} }
              @keyframes float4 { 0%,100%{transform:translate(0,0)} 33%{transform:translate(-20px,-10px)} 66%{transform:translate(12px,12px)} }
              @keyframes mapZoom { 0%,100%{transform:scale(1)} 50%{transform:scale(1.35)} }
              @keyframes mapPulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
            `}</style>

            {/* SE BUSCA - 3/4 width */}
            <Link to="/wants" className="relative flex-1 overflow-hidden bg-[#1C1C1C] hover:bg-black py-4 px-6 text-center transition-colors"
              style={{border: '2px solid #e53e3e', boxShadow: '0 0 12px 2px rgba(229,62,62,0.5)'}}>
              <span className="absolute top-[-20px] left-[10%] w-16 h-16 rounded-full bg-primary opacity-40 blur-md" style={{animation:'float1 4.2s ease-in-out infinite'}} />
              <span className="absolute bottom-[-10px] left-[35%] w-12 h-12 rounded-full bg-yellow-400 opacity-30 blur-md" style={{animation:'float2 5s ease-in-out infinite'}} />
              <span className="absolute top-[-10px] right-[15%] w-20 h-20 rounded-full bg-primary opacity-30 blur-md" style={{animation:'float3 3.8s ease-in-out infinite'}} />
              <span className="absolute bottom-[-5px] right-[5%] w-10 h-10 rounded-full bg-orange-400 opacity-30 blur-sm" style={{animation:'float4 4.6s ease-in-out infinite'}} />
              <span className="relative text-3xl md:text-4xl font-black text-white uppercase tracking-wide">
                {homepage.seBusca?.title || DEFAULT_HOMEPAGE.seBusca.title}
              </span>
              <p className="relative text-gray-300 mt-1 text-sm uppercase tracking-widest">
                {homepage.seBusca?.subtitle || DEFAULT_HOMEPAGE.seBusca.subtitle}
              </p>
            </Link>

            {/* Mapa de Servicios - 1/4 width */}
            <Link to="/talleres"
              className="relative overflow-hidden flex flex-col items-center justify-center gap-1 bg-[#0f2027] hover:bg-[#1a3a4a] transition-colors px-4 py-4 text-white shrink-0"
              style={{width:'25%', border:'2px solid #38bdf8', boxShadow:'0 0 10px 1px rgba(56,189,248,0.4)'}}>
              {/* Animated map icon */}
              <div className="relative w-10 h-10 flex items-center justify-center">
                {/* Zoom rings */}
                <span className="absolute w-10 h-10 rounded-full border-2 border-sky-400 opacity-60" style={{animation:'mapZoom 2.5s ease-in-out infinite'}} />
                <span className="absolute w-6 h-6 rounded-full border-2 border-sky-300 opacity-80" style={{animation:'mapZoom 2.5s ease-in-out infinite 0.4s'}} />
                {/* Map pin */}
                <span className="relative text-xl" style={{animation:'mapPulse 2.5s ease-in-out infinite'}}>🗺️</span>
              </div>
              <span className="text-xs font-black uppercase tracking-wide text-sky-300 text-center leading-tight">Mapa de<br/>Servicios</span>
            </Link>
          </div>
        </section>
      )}

      {/* Brands */}
      <section className="py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-black uppercase tracking-widest text-gray-700">Marcas</h2>
            <Link to="/products" className="text-xs font-bold text-primary uppercase tracking-wide hover:underline">
              Ver todas →
            </Link>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {brands.map(brand => (
              <Link
                key={brand}
                to={`/products?brand=${brand}`}
                className="shrink-0 bg-white text-gray-800 font-semibold px-4 py-1.5 border border-gray-300 hover:bg-primary hover:text-white hover:border-primary transition text-xs uppercase tracking-wide"
              >
                {brand}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-6 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-black uppercase tracking-wide">🔥 Productos Destacados</h2>
              <p className="text-gray-500 text-xs">Los más vistos esta semana</p>
            </div>
            <Link to="/products" className="hidden md:inline-block text-xs font-bold text-white bg-primary px-4 py-2 uppercase tracking-wide hover:bg-primary-dark transition-colors">
              Ver todos
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="bg-white animate-pulse border border-gray-200">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-3 space-y-1">
                    <div className="h-3 bg-gray-200 w-3/4" />
                    <div className="h-4 bg-gray-200 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : featured.length > 0 ? (
            <>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {featured.map(product => (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}`}
                    className="bg-white border border-gray-200 hover:border-gray-400 hover:shadow-md transition-all group"
                  >
                    <div className="aspect-square bg-gray-100 flex items-center justify-center relative overflow-hidden">
                      {product.images?.[0]?.url ? (
                        <img src={product.images[0].url} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <span className="text-3xl">🛞</span>
                      )}
                      {product.condition === 'NEW' && (
                        <span className="absolute top-0 left-0 bg-green-600 text-white text-xs px-1.5 py-0.5 font-bold uppercase">Nuevo</span>
                      )}
                      {product.is_featured && (
                        <span className="absolute top-0 right-0 bg-yellow-400 text-black text-xs px-1.5 py-0.5 font-bold">⭐</span>
                      )}
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-medium truncate text-gray-700">{product.title}</h3>
                      <p className="text-sm font-black text-primary">₡{product.price?.toLocaleString()}</p>
                    </div>
                  </Link>
                ))}
              </div>
              {featuredHasMore && (
                <div className="mt-5 text-center">
                  <button
                    onClick={loadMoreFeatured}
                    disabled={loadingMore}
                    className="bg-[#1C1C1C] text-white font-bold px-8 py-2.5 uppercase text-sm tracking-wide hover:bg-black disabled:opacity-50 transition-colors"
                  >
                    {loadingMore ? 'Cargando...' : '⟳ Más productos'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-white border border-gray-200">
              <p className="text-gray-500">No hay productos aún.</p>
              <Link to="/products" className="btn-primary mt-4 inline-block">Ver productos</Link>
            </div>
          )}
        </div>
      </section>

      {/* Accesorios */}
      <section className="py-6 bg-white border-t border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          {/* Banner - Accesorios clickeable */}
          <a href="/accessories" className="block relative rounded-xl overflow-hidden mb-5 h-36 md:h-48 group cursor-pointer">
            <img src="https://images.unsplash.com/photo-1626275850959-2f19cd600b0f?q=80&w=1175&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8" alt="Accesorios para moto"
              className="w-full h-full object-cover transition-all duration-300 group-hover:scale-110 group-hover:brightness-110" />
            {/* Overlay oscuro */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/80" />
            {/* Overlay animado con ruido al hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,107,0,0.1) 2px, rgba(255,107,0,0.1) 4px)'}} />
            <div className="absolute inset-0 flex items-center justify-between px-6 z-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-black uppercase tracking-wide text-white">🧤 Accesorios</h2>
                <p className="text-gray-300 text-sm">Cascos, guantes, chaquetas y más</p>
              </div>
              <span className="text-xs font-bold text-black bg-primary px-4 py-2 uppercase tracking-wide hover:bg-primary-dark transition-colors rounded-full">
                Ver todos →
              </span>
            </div>
            {/* Borde al hover */}
            <div className="absolute inset-0 border-4 border-primary/0 group-hover:border-primary/80 rounded-xl transition-all duration-300 pointer-events-none" />
            {/* Efecto de ruido/estática al hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
              style={{animation: 'noise 0.2s steps(2) infinite'}} />
            <style>{`
              @keyframes noise {
                0% { background-position: 0 0; filter: hue-rotate(0deg); }
                25% { background-position: 5px 5px; filter: hue-rotate(10deg); }
                50% { background-position: -5px 10px; filter: hue-rotate(-10deg); }
                75% { background-position: 10px -5px; filter: hue-rotate(5deg); }
                100% { background-position: 0 0; filter: hue-rotate(0deg); }
              }
            `}</style>
          </a>

          {/* Products grid */}
          {promotedAccessories.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {promotedAccessories.slice(0, 12).map(p => (
                  <Link key={p.id} to={`/products/${p.id}`}
                    className="bg-white border border-gray-200 hover:border-gray-400 hover:shadow-md transition-all group">
                    <div className="aspect-square bg-gray-100 overflow-hidden relative">
                      {p.images?.[0]?.url
                        ? <img src={p.images[0].url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        : <span className="w-full h-full flex items-center justify-center text-3xl">🧤</span>
                      }
                      {p.condition === 'NEW' && <span className="absolute top-0 left-0 bg-green-600 text-white text-xs px-1.5 py-0.5 font-bold uppercase">Nuevo</span>}
                      {(p.is_featured) && (
                        <span className="absolute top-0 right-0 bg-yellow-400 text-black text-xs px-1.5 py-0.5 font-bold">⭐</span>
                      )}
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-medium truncate text-gray-700">{p.title}</h3>
                      <p className="text-sm font-black text-primary">₡{p.price?.toLocaleString()}</p>
                    </div>
                  </Link>
              ))}
            </div>
          )}
          {promotedAccessories.length === 0 && accessories.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {accessories.slice(0, 6).map(p => (
                  <Link key={p.id} to={`/products/${p.id}`}
                    className="bg-white border border-gray-200 hover:border-gray-400 hover:shadow-md transition-all group">
                    <div className="aspect-square bg-gray-100 overflow-hidden relative">
                      {p.images?.[0]?.url
                        ? <img src={p.images[0].url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        : <span className="w-full h-full flex items-center justify-center text-3xl">🧤</span>
                      }
                      {p.condition === 'NEW' && <span className="absolute top-0 left-0 bg-green-600 text-white text-xs px-1.5 py-0.5 font-bold uppercase">Nuevo</span>}
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-medium truncate text-gray-700">{p.title}</h3>
                      <p className="text-sm font-black text-primary">₡{p.price?.toLocaleString()}</p>
                    </div>
                  </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Consumables */}
      {/* Consumibles */}
      <section className="py-6 bg-white border-t border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          {/* Banner - Consumibles clickeable */}
          <a href="/consumables" className="block relative rounded-xl overflow-hidden mb-5 h-36 md:h-48 group cursor-pointer">
            <img src="https://images.unsplash.com/photo-1771581136070-5322586211d6?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Consumibles para moto"
              className="w-full h-full object-cover transition-all duration-300 group-hover:scale-110 group-hover:brightness-110" />
            {/* Overlay oscuro */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/80" />
            {/* Overlay animado con ruido al hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,107,0,0.1) 2px, rgba(255,107,0,0.1) 4px)'}} />
            <div className="absolute inset-0 flex items-center justify-between px-6 z-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-black uppercase tracking-wide text-white">🛢️ Consumibles</h2>
                <p className="text-gray-300 text-sm">Aceites, filtros, bujías, cadenas y más</p>
              </div>
              <span className="text-xs font-bold text-black bg-primary px-4 py-2 uppercase tracking-wide hover:bg-primary-dark transition-colors rounded-full">
                Ver todos →
              </span>
            </div>
            {/* Borde al hover */}
            <div className="absolute inset-0 border-4 border-primary/0 group-hover:border-primary/80 rounded-xl transition-all duration-300 pointer-events-none" />
            {/* Efecto de ruido/estática al hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
              style={{animation: 'noise 0.2s steps(2) infinite'}} />
          </a>
        </div>
      </section>

      {/* New Products */}
      <section className="py-6 bg-white border-t border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-black uppercase tracking-wide">✨ Nuevos Ingresos</h2>
              <p className="text-gray-500 text-xs">Recién publicados por vendedores</p>
            </div>
            <Link to="/products?sort=newest" className="hidden md:inline-block text-xs font-bold text-white bg-primary px-4 py-2 uppercase tracking-wide hover:bg-primary-dark transition-colors">
              Ver todos →
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-100 animate-pulse border border-gray-200">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-3 space-y-1">
                    <div className="h-3 bg-gray-200 w-3/4" />
                    <div className="h-4 bg-gray-200 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : newProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {newProducts.slice(0, 8).map(product => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="bg-white border border-gray-200 hover:border-gray-400 hover:shadow-md transition-all group"
                >
                  <div className="aspect-square bg-gray-100 flex items-center justify-center relative overflow-hidden">
                    {product.images?.[0]?.url ? (
                      <img src={product.images[0].url} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <span className="text-3xl">🛞</span>
                    )}
                    <span className="absolute top-0 left-0 bg-green-600 text-white text-xs px-1.5 py-0.5 font-bold uppercase">Nuevo</span>
                    {(product.is_featured) && (product.seller?.subscription_plan === 'PRO' || product.seller?.subscription_plan === 'PREMIUM') && (
                      <span className="absolute top-0 right-0 bg-primary text-white text-xs px-1.5 py-0.5 font-bold">⭐</span>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-xs font-semibold text-gray-700 truncate">{product.title}</h3>
                    <p className="text-sm font-black text-primary">₡{product.price?.toLocaleString()}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 border border-gray-200">
              <p className="text-gray-500">No hay productos nuevos aún.</p>
            </div>
          )}
        </div>
      </section>

      {/* Talleres cerca de mí */}
      <section className="py-6 bg-[#F5F5F5] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-black uppercase tracking-wide">🔧 Talleres y Repuesteras</h2>
              <p className="text-gray-500 text-xs">Negocios registrados en MotoMarket</p>
            </div>
            <Link to="/talleres" className="text-xs font-bold text-white bg-primary px-4 py-2 uppercase tracking-wide hover:bg-primary-dark transition-colors">
              Ver mapa completo →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {shops.length === 0 ? (
              <Link to="/talleres" className="col-span-full flex items-center justify-center gap-3 bg-white border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-primary transition text-gray-500 hover:text-primary">
                <span className="text-3xl">🗺️</span>
                <span className="font-semibold">Ver mapa de talleres y repuesteras →</span>
              </Link>
            ) : shops.slice(0, 6).map(s => (
              <Link key={s.id} to="/talleres"
                className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:border-primary hover:shadow-md transition">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 ${s.shop_type === 'TALLER' ? 'bg-red-500' : s.shop_type === 'GRUA' ? 'bg-orange-500' : 'bg-blue-500'}`}>
                  {s.shop_type === 'TALLER' ? '🔧' : s.shop_type === 'GRUA' ? '🚛' : '🛞'}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{s.name}</p>
                  <p className="text-xs text-gray-400 truncate">{s.shop_address || (s.shop_type === 'TALLER' ? 'Taller' : 'Repuestera')}</p>
                </div>
              </Link>
            ))}
            {shops.length > 6 && (
              <Link to="/talleres" className="flex items-center justify-center gap-2 bg-gray-50 border border-gray-200 rounded-xl p-4 hover:border-primary transition text-sm font-semibold text-gray-500 hover:text-primary">
                +{shops.length - 6} más → Ver mapa
              </Link>
            )}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Taller</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block" /> Repuestera</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      {homepage.cta?.enabled !== false && (
        <section className="bg-[#1C1C1C] text-white py-10">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-black uppercase tracking-tight mb-2">{homepage.cta?.title || DEFAULT_HOMEPAGE.cta.title}</h2>
            <p className="text-gray-400 text-sm mb-5">{homepage.cta?.subtitle || DEFAULT_HOMEPAGE.cta.subtitle}</p>
            <Link to="/register" className="btn-primary inline-block">
              {homepage.cta?.buttonText || DEFAULT_HOMEPAGE.cta.buttonText}
            </Link>
          </div>
        </section>
      )}

      {/* Explore by brand */}
      <section className="py-6 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-lg font-black uppercase tracking-wide mb-4">Explora por marca</h2>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {brands.map(brand => (
              <Link
                key={brand}
                to={`/products?brand=${brand}`}
                className="shrink-0 bg-[#F5F5F5] border border-gray-300 px-5 py-3 text-center hover:bg-primary hover:text-white hover:border-primary transition-all"
              >
                <span className="text-xl block mb-1">🏍️</span>
                <h3 className="font-bold text-xs uppercase tracking-wide">{brand}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
